import classNames from 'classnames';
import type { MutableRefObject } from 'react';
import React, { useMemo } from 'react';
import { createPortal } from 'react-dom';

import type { ImMNElement } from '../../../../common/elements/elementModelTypes';
import type { CellEditingSession } from '../../../../common/table/TableTypes';

import type { MilanoteEditorRef } from '../../../components/editor/milanoteEditor/MilanoteEditorTypes';

import { getBackgroundColorClass } from '../utils/dataUtils/cell/tableCellStyleUtils';
import { getTableCellVerticalAlignmentClassName } from '../utils/domUtils/tableCellVerticalAlignmentClassNameUtils';
import { getHotTableCellData } from '../utils/hotInstanceUtils/meta/hotTableCellMetaUtils';
import type { MNHotInstance } from '../tableHotInstanceTypes';
import DraftCellEditorComponent from './DraftCellEditorComponent';

type MilanoteCellEditorComponentProps = {
    element: ImMNElement;
    elementId: string;
    isEditable: boolean;
    cellEditingSession: CellEditingSession | null;
    currentlyEditingEditorId: string | null;
    hotTableContainerRef: MutableRefObject<HTMLDivElement | null>;
    hotTableInstanceRef: MutableRefObject<MNHotInstance | null>;
    cellEditorRef: MutableRefObject<MilanoteEditorRef | null>;
    locale: string;
    gridSize: number;
    mounted: boolean;
};

const MilanoteCellEditorComponent: React.FC<MilanoteCellEditorComponentProps> = ({
    element,
    elementId,
    isEditable,
    cellEditingSession,
    currentlyEditingEditorId,
    hotTableContainerRef,
    hotTableInstanceRef,
    cellEditorRef,
    locale,
}) => {
    const hotTableInstance = hotTableInstanceRef.current;
    const cellData =
        hotTableInstance && cellEditingSession
            ? getHotTableCellData(hotTableInstance, cellEditingSession.cellCoords)
            : undefined;

    const backgroundClasses = useMemo(() => getBackgroundColorClass(cellData), [cellData]);

    const portalTarget = hotTableInstanceRef.current?.rootElement || hotTableContainerRef.current;

    if (!portalTarget) return null;

    return createPortal(
        <div className={classNames('MilanoteCellEditorComponent', backgroundClasses)}>
            <div className="MilanoteCellEditorComponent-relative">
                <div className="handsontable-input-positioner">
                    <div
                        className={classNames(
                            'handsontable-input-holder',
                            getTableCellVerticalAlignmentClassName(cellData?.verticalAlignment),
                        )}
                    >
                        <DraftCellEditorComponent
                            element={element}
                            elementId={elementId}
                            isEditable={isEditable}
                            cellData={cellData}
                            cellEditingSession={cellEditingSession}
                            currentlyEditingEditorId={currentlyEditingEditorId}
                            hotTableInstanceRef={hotTableInstanceRef}
                            cellEditorRef={cellEditorRef}
                            locale={locale}
                        />
                    </div>
                </div>
            </div>
        </div>,
        portalTarget,
    );
};

export default MilanoteCellEditorComponent;
